<template>
  <section v-if="answers.length" class="panel question-result-details">
    <h2><slot name="title">{{ title }}</slot></h2>
    <div class="answer-list">
      <article
        v-for="(answer, index) in answers"
        :key="answerKey(answer, index)"
        class="answer-item"
        :class="answerClass(answer)"
      >
        <header class="answer-header">
          <span>第 {{ index + 1 }} 题</span>
          <small v-if="showType && typeWord(answer)" class="answer-type">
            {{ typeWord(answer) }}
          </small>
          <em
            v-if="status(answer)"
            :class="`answer-status is-${status(answer).kind}`"
          >
            {{ status(answer).text }}
          </em>
          <small v-if="showQuestionId" class="answer-question-id">
            题目ID: {{ answer.question_id }}
          </small>
          <slot name="header-extra" :answer="answer" :index="index" :status="status(answer)" />
          <strong v-if="showScore">{{ scoreText(answer) }}</strong>
        </header>

        <div v-if="questionText(answer)" class="question-text" v-html="questionText(answer)" />

        <section
          v-for="section in displaySections(answer)"
          :key="section.title"
          class="question-section"
        >
          <div class="question-section__title">{{ section.title }}</div>
          <div class="question-section__body" v-html="section.html" />
        </section>

        <div v-if="showOptions && displayOptions(answer).length" class="option-list">
          <div
            v-for="option in displayOptions(answer)"
            :key="option.key"
            :class="['option-item', optionClass(answer, option.key)]"
          >
            <span class="option-key">{{ option.key }}</span>
            <span v-html="option.content" />
            <small v-if="includesAnswer(answer.member_answer, option.key)">我的答案</small>
            <small v-if="includesAnswer(answer.correct_answer, option.key)">正确答案</small>
          </div>
        </div>

        <slot name="before-answer-values" :answer="answer" :index="index" />
        <div class="answer-values" :class="{ 'is-single': !showCorrectAnswer }">
          <div>
            <span>{{ memberAnswerLabel }}</span>
            <slot name="member-answer" :answer="answer" :text="memberAnswerText(answer)">
              <strong>{{ memberAnswerText(answer) }}</strong>
            </slot>
          </div>
          <div v-if="showCorrectAnswer">
            <span>{{ resolvedCorrectAnswerLabel(answer) }}</span>
            <slot
              name="correct-answer"
              :answer="answer"
              :label="resolvedCorrectAnswerLabel(answer)"
              :text="correctAnswerText(answer)"
            >
              <strong>{{ correctAnswerText(answer) }}</strong>
            </slot>
          </div>
        </div>

        <div v-if="showAnalysis && answer.analysis" class="answer-analysis">
          <span>答案解析</span>
          <slot name="analysis" :answer="answer" :text="analysisText(answer.analysis)">
            <div>{{ analysisText(answer.analysis) }}</div>
          </slot>
        </div>
        <slot name="item-footer" :answer="answer" :index="index" />
      </article>
    </div>
  </section>
</template>

<script>
import {
  assessmentMaxSelections,
  buildQuestionDisplaySections,
  formatQuestionAnswer,
  getQuestionTitle,
  getQuestionType,
  getQuestionTypeWord,
  isQuestionAnswerEmpty,
  normalizeQuestionAnswerValues,
  questionOptions
} from '@/utils/questionDisplay'

export default {
  name: 'QuestionResultDetails',
  props: {
    answers: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: '答题详情'
    },
    showType: {
      type: Boolean,
      default: true
    },
    showStatus: {
      type: Boolean,
      default: true
    },
    showScore: {
      type: Boolean,
      default: true
    },
    showCorrectAnswer: {
      type: Boolean,
      default: true
    },
    showQuestionId: {
      type: Boolean,
      default: false
    },
    showOptions: {
      type: Boolean,
      default: false
    },
    showAnalysis: {
      type: Boolean,
      default: true
    },
    memberAnswerLabel: {
      type: String,
      default: '你的答案'
    },
    memberAnswerEmptyText: {
      type: String,
      default: '未作答'
    },
    correctAnswerLabel: {
      type: String,
      default: '正确答案'
    },
    correctAnswerEmptyText: {
      type: String,
      default: '未作答'
    }
  },
  methods: {
    answerKey(answer, index) {
      return `${answer.question_id || 'question'}-${index}`
    },
    questionText(answer) {
      return getQuestionTitle(answer)
    },
    typeWord(answer) {
      return answer.question_type_word || getQuestionTypeWord(answer)
    },
    displaySections(answer) {
      return buildQuestionDisplaySections(answer, {
        showAnswers: true,
        hideInteractiveBlocks: false
      })
    },
    status(answer) {
      if (!this.showStatus) return null

      const isAssessment = getQuestionType(answer) === 8
      if (isQuestionAnswerEmpty(answer.member_answer, answer)) {
        return { text: isAssessment ? '未完成' : '未作答', kind: 'unanswered' }
      }
      if (answer.is_correct === null || typeof answer.is_correct === 'undefined') {
        return { text: '待批改', kind: 'pending' }
      }
      if (isAssessment) {
        return Number(answer.is_correct) === 1
          ? { text: '已完成', kind: 'correct' }
          : { text: '无效作答', kind: 'wrong' }
      }
      return Number(answer.is_correct) === 1
        ? { text: '回答正确', kind: 'correct' }
        : { text: '回答错误', kind: 'wrong' }
    },
    answerClass(answer) {
      const value = this.status(answer)
      return value ? { [`is-${value.kind}`]: true } : {}
    },
    scoreText(answer) {
      const total = Number(answer.score) || 0
      if (answer.awarded_score === null || typeof answer.awarded_score === 'undefined') {
        return `待批改 / ${total} 分`
      }
      return `${Number(answer.awarded_score) || 0} / ${total} 分`
    },
    memberAnswerText(answer) {
      return formatQuestionAnswer(answer.member_answer, this.memberAnswerEmptyText, answer)
    },
    resolvedCorrectAnswerLabel(answer) {
      return getQuestionType(answer) === 8 ? '完成规则' : this.correctAnswerLabel
    },
    correctAnswerText(answer) {
      if (getQuestionType(answer) === 8) {
        const maxSelections = assessmentMaxSelections(answer)
        return maxSelections > 0
          ? `选择 1 至 ${maxSelections} 个有效选项即视为完成`
          : '选择至少 1 个有效选项即视为完成'
      }
      return formatQuestionAnswer(
        answer.correct_answer,
        this.correctAnswerEmptyText,
        answer
      )
    },
    displayOptions(answer) {
      if (getQuestionType(answer) === 6) return []
      return questionOptions(answer, answer)
    },
    includesAnswer(answer, key) {
      return normalizeQuestionAnswerValues(answer).includes(String(key))
    },
    optionClass(answer, key) {
      return {
        'is-member': this.includesAnswer(answer.member_answer, key),
        'is-correct': this.includesAnswer(answer.correct_answer, key)
      }
    },
    analysisText(analysis) {
      return String(analysis || '')
        .replace(/<br\s*\/?\s*>/gi, '\n')
        .replace(/<\/p\s*>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n\s+/g, '\n')
        .trim()
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/tokens';

.question-result-details.panel {
  padding: 28px;
  margin-bottom: 20px;
}

.question-result-details h2 {
  margin-bottom: 20px;
  font-size: 22px;
}

.answer-list {
  display: grid;
  gap: 16px;
}

.answer-item {
  display: grid;
  gap: 14px;
  padding: 18px;
  border: 1px solid $color-border;
  border-radius: var(--radius-md);
  background: $color-surface;
}

.answer-item.is-correct {
  border-color: rgba($color-success, 0.32);
  background: rgba($color-success, 0.04);
}

.answer-item.is-wrong {
  border-color: rgba($color-danger, 0.26);
  background: rgba($color-danger, 0.035);
}

.answer-item.is-unanswered {
  border-color: $color-border;
  background: $color-surface;
}

.answer-item.is-pending {
  border-color: rgba($color-warning, 0.3);
  background: rgba($color-warning, 0.04);
}

.answer-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid $color-border-light;
}

.answer-header > span {
  color: $color-primary;
  font-weight: 800;
}

.answer-type,
.answer-status {
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  font-weight: 700;
}

.answer-type {
  background: rgba($color-info, 0.1);
  color: $color-info;
}

.answer-status {
  font-size: 12px;
  font-style: normal;
}

.answer-status.is-correct {
  background: rgba($color-success, 0.1);
  color: $color-success;
}

.answer-status.is-wrong {
  background: rgba($color-danger, 0.1);
  color: $color-danger;
}

.answer-status.is-unanswered {
  background: rgba($color-text-secondary, 0.1);
  color: $color-text-secondary;
}

.answer-status.is-pending {
  background: rgba($color-warning, 0.1);
  color: $color-warning;
}

.answer-question-id {
  margin-left: auto;
  color: $color-text-secondary;
  font-size: 12px;
}

.answer-header strong {
  margin-left: auto;
  color: $color-warning;
}

.answer-question-id + strong {
  margin-left: 0;
}

.question-text {
  color: $color-text-primary;
  line-height: 1.8;
  word-break: break-word;
}

.question-section {
  display: grid;
  gap: 6px;
}

.question-section__title {
  color: $color-text-secondary;
  font-size: 13px;
  font-weight: 800;
}

.question-section__body {
  padding: 12px 14px;
  border: 1px solid $color-border-light;
  border-radius: var(--radius-md);
  background: $color-background;
  color: $color-text-secondary;
  line-height: 1.7;
  word-break: break-word;
}

.option-list {
  display: grid;
  gap: 10px;
}

.option-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px;
  border: 1px solid $color-border;
  border-radius: var(--radius-md);
}

.option-item.is-member {
  border-color: $color-warning;
  background: rgba($color-warning, 0.08);
}

.option-item.is-correct {
  border-color: $color-success;
  background: rgba($color-success, 0.08);
}

.option-key {
  flex: none;
  font-weight: 700;
}

.option-item small {
  margin-left: auto;
  color: $color-text-secondary;
}

.answer-values {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.answer-values.is-single {
  grid-template-columns: 1fr;
}

.answer-values > div {
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-radius: var(--radius-md);
  background: rgba($color-surface, 0.78);
}

.answer-values span {
  color: $color-text-secondary;
  font-size: 13px;
  font-weight: 700;
}

.answer-values strong {
  color: $color-text-primary;
  font-size: 15px;
  word-break: break-word;
}

.answer-analysis {
  display: grid;
  gap: 8px;
  padding: 14px;
  border-radius: var(--radius-md);
  background: rgba($color-info, 0.06);
}

.answer-analysis > span {
  color: $color-info;
  font-size: 13px;
  font-weight: 800;
}

.answer-analysis > div {
  color: $color-text-secondary;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 768px) {
  .question-result-details.panel {
    padding: 20px;
  }

  .answer-values {
    grid-template-columns: 1fr;
  }

  .answer-header strong,
  .answer-question-id {
    margin-left: 0;
  }
}
</style>
